import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { Button } from '../../../../components/button/Button';
import { FlyoutFooter } from '../../../../components/flyout-components/FlyoutFooter';
import { TextInput } from '../../../../components/form-elements/_index';
import { ProductionSvc } from '../../../../production/service';
import { StatusCode } from '../../../../utils/api';

interface IProps {
    isOpen: boolean;

    /** Called when the flyout is closed, with whether a run was created, so that the planning can be fetched again */
    onClose: (created: boolean) => void;
}

/**
 * Create a production run from the order IDs in an Excel spreadsheet that was uploaded: one column of them, with no
 * header row.  When any of the orders cannot go into a run the API says why, which is toasted, and the flyout stays open
 * so that the spreadsheet can be put right and chosen again.
 */
export const CreateRunForOrderIdsFlyout = (props: IProps) => {

    const [service] = React.useState(new ProductionSvc());
    const [name, setName] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const [isCreating, setIsCreating] = React.useState(false);

    // A new input each time the flyout is opened, as a file input cannot be emptied from here
    const [inputKey, setInputKey] = React.useState(0);

    /**
     * Start with nothing entered each time the flyout is opened
     */
    React.useEffect(() => {
        if (props.isOpen) {
            setName('');
            setFile(null);
            setIsCreating(false);
            setInputKey(key => key + 1);
        }
    }, [props.isOpen]);

    const createRun = async () => {
        if (!file) {
            return;
        }

        setIsCreating(true);
        const result = await service.createProductionRunForOrderIds(name.trim(), file);

        setIsCreating(false);
        if (result.status === StatusCode.Success) {
            props.onClose(true);
        }
    };

    const onNameChange = (e: any) => {
        if (e && e.target && e.target.value !== undefined) {
            setName(e.target.value);
        }
    };

    const onFileChange = (e: any) => {
        const chosen = e && e.target && e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        setFile(chosen);
    };

    // The run that is being created is waited for: closing now would leave it to close the flyout when it is opened again,
    // and to throw away what was entered then
    const dismiss = () => {
        if (!isCreating) {
            props.onClose(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            onClose={ dismiss }
            open={ props.isOpen }>
            <div className="stop-action-drawer stop-action-reorder-route production-module-flyout">
                <div className="row">
                    <div className="col">
                        <h1>Create Run from Order IDs</h1>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <p>
                            Upload an Excel spreadsheet (.xlsx or .xls) with the IDs of the orders in the run in its
                            first column, one to a row and with no header row.
                            If any of the orders cannot be added to a production run, no run is created and you are
                            told why.
                        </p>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <TextInput
                            id="planning-run-name"
                            type="text"
                            label="Name"
                            name="name"
                            value={ name }
                            onChange={ onNameChange }
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="order-ids-file">
                            <label htmlFor="planning-run-order-ids-file">
                                <i className="la la-upload" />
                                {
                                    file
                                        ? <span className="order-ids-file-name">{ file.name }</span>
                                        : <span>Choose a spreadsheet...</span>
                                }
                            </label>
                            <input
                                key={ inputKey }
                                id="planning-run-order-ids-file"
                                type="file"
                                name="file"
                                accept=".xlsx,.xls"
                                onChange={ onFileChange } />
                        </div>
                    </div>
                </div>

                <FlyoutFooter>
                    <div className="row">
                        <div className="col">
                            <Button
                                text="Create Run"
                                classes="attention"
                                testID="create-run-from-order-ids"
                                disable={ isCreating || name.trim().length === 0 || !file }
                                onBtnClick={ createRun } />
                        </div>
                    </div>
                </FlyoutFooter>

            </div>
        </Drawer>
    );

};
